import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderBooking, ParkingSite, ParkingSiteSpotBooking, ParkingVM } from '../../../../models/parkingsite.model';
import { ParkingJobService } from '../../../../services/parkingjob.service';
import { CheckoutService } from '../../../../services/checkout.service';
import { ParkingEditComponent } from './parking-edit/parking-edit.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { MyConfigService } from '../../../../services/my-config.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { DialogConfirmComponent } from '../../../../components/dialog-confirm/dialog-confirm.component';

@Component({
  selector: 'app-parking',
  templateUrl: './parking.component.html',
  styleUrls: ['./parking.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ParkingComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  activeRequests: number = 0;
  organizationId: string;
  parkingId: string;
  dateFrom: string;
  dateTo: string;
  dateSelectorGroup: UntypedFormGroup;
  parkingSite: ParkingSite;
  dataSource: ParkingSite;
  orderParkingId = '?';
  booking: ParkingSiteSpotBooking;
  // parkingVMs: ParkingVM[] = [];
  isParkingBookingMode = true;
  isParkingCheckinMode = false;
  @Input() organization: MyConfigOrganization
  private orgSubscription: Subscription = new Subscription();
  rawOverlappingParkings: any[] = [];
  overlappingParkings: any[] = [];
  overlappingBookingFromRange!: UntypedFormGroup;
  overlappingBookingToRange!: UntypedFormGroup;
  overlappingSearchText: string = '';
  overlappingSearchField: string = 'parkingName';
  targetScrollSpot: string | null = null;
  displayedColumnsOverlapping: string[] = [
    'parkingName',
    'parkingRowName',
    'parkingSpotName',
    'carId',
    'bookedFrom',
    'bookedTo'
  ];

  displayedColumns: string[] = [
    'parkingId',
    'dateFrom',
    'dateTo',
    'availableParkingSpots',
    'activeParkingSpots',
    'disabledParkingSpots',
    'datePicker'
  ];
  statusContext: { type: 'row' | 'spot', data: any, rowData?: any } | null = null;
  componentDestroyed$: Subject<boolean> = new Subject();

  parkingFeatures = [
    "{ \"en\": \"Enclosed, monitored area\", \"sv\": \"Inhängnat, bevakat område\" }",
    "{ \"en\": \"Transfer from or near the car, monitored area\", \"sv\": \"Transfer från eller nära bilen\" }",
    "{ \"en\": \"Staffed around the clock, monitored area\", \"sv\": \"Bemannat dygnet runt\" }",
    "{ \"en\": \"Asphalted parking, monitored area\", \"sv\": \"Asfalterad parkering\" }",
    "{ \"en\": \"Clear parking spaces - 2.5x5.0 m\", \"sv\": \"Tydliga parkeringsrutor - 2.5x5.0 m\" }",
  ];
  constructor(
    private fb: UntypedFormBuilder,
    private checkoutService: CheckoutService,
    private parkingJobService: ParkingJobService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private myConfigService: MyConfigService,
    public backOffice24StatesService: BackOffice24StatesService,
    private snackBar: MatSnackBar,
    private backOfficeService: BackOfficeService,
  ) { }

  ngOnInit(): void {
    try {
      this.getOrganizationId();
      this.initializeForm();
    } catch (e) {
      console.log(e);
    }
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }


  getOrganizationId() {
    // this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
    //   if(params['organizationId'] != null && params['organizationId'] != '' && params['organizationId'] != undefined && params['organizationId'] != 'undefined')
    //   this.organizationId = params['organizationId'];
    // });
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org;
        if (this.organization) {
          this.loadOverlappingParkings();
        }
      }
    );

    if (this.organization == null || this.organization == undefined) {
      this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
        console.log('params', params);
        if (params['organizationId'] != null && params['organizationId'] != '' && params['organizationId'] != undefined && params['organizationId'] != 'undefined') {
          this.organizationId = params['organizationId'];
          this.organization = this.myConfigService.getMyConfigOrganizationById(this.organizationId.toString().toLowerCase());
          if (this.organization) {
            this.loadOverlappingParkings();
          }
        } else {
          // this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
          //   org => this.organization = org
          // );
        }
      });
    }
    if (this.organization && this.organization.config && this.organization.config.parkings) {
      this.organization.config.parkings.forEach(parking => {
        parking.parkingFeatures = this.parkingFeatures
      });
    }
  }

  initializeForm() {

    this.dateFrom = moment().format();
    this.dateTo = moment().add(10, 'd').format();

    this.dateSelectorGroup = this.fb.group({
      dateFrom: [this.dateFrom],
      dateTo: [this.dateTo]
    });

    this.overlappingBookingFromRange = this.fb.group({
      start: [null],
      end: [null]
    });

    this.overlappingBookingToRange = this.fb.group({
      start: [null],
      end: [null]
    });

    this.overlappingBookingFromRange.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.applyOverlappingFilters();
      });

    this.overlappingBookingToRange.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.applyOverlappingFilters();
      });
  }

  getParkingSite() {

    if (this.isParkingBookingMode) {
      this.getParkingSiteBooking();
    }

    if (this.isParkingCheckinMode) {
      this.getParkingSiteCheckin();
    }
  }

  getParkingSiteCheckin() {
    this.incrementRequests();
    this.parkingJobService
      .getParkingSiteCheckin(this.parkingId /*, moment(this.dateFrom).format('YYYY-MM-DD'), moment(this.dateTo).format('YYYY-MM-DDT23:59:59')*/)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: result => {
          if (result) {
            result.parkingSiteRows.forEach(row => {
              row.parkingSiteSpots.sort((a, b) => {
                const parkingSiteNameA = +a?.parkingSiteName?.split(':')?.pop();
                const parkingSiteNameB = +b?.parkingSiteName?.split(':')?.pop();
                if (parkingSiteNameA < parkingSiteNameB) {
                  return -1;
                }
                if (parkingSiteNameA > parkingSiteNameB) {
                  return 1;
                }
                return 0;
              });
            });
            this.parkingSite = result;
            this.checkScrollToSpot();
          } else {
            this.parkingSite = null;
            if (this.targetScrollSpot) {
              this.targetScrollSpot = null;
              this.decrementRequests();
            }
          }
          this.decrementRequests();
        },
        error: error => {
          console.error('Error fetching check-in sites:', error);
          this.parkingSite = null;
          if (this.targetScrollSpot) {
            this.targetScrollSpot = null;
            this.decrementRequests();
          }
          this.decrementRequests();
        }
      });
  }

  getParkingSiteBooking() {
    this.incrementRequests();
    this.parkingJobService
      .getParkingSiteFromTo(this.parkingId, moment(this.dateFrom).format('YYYY-MM-DD'), moment(this.dateTo).format('YYYY-MM-DDT23:59:59'))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: result => {
          if (result) {
            result.parkingSiteRows.forEach(row => {
              row.parkingSiteSpots.sort((a, b) => {
                const parkingSiteNameA = +a?.parkingSiteName?.split(':')?.pop();
                const parkingSiteNameB = +b?.parkingSiteName?.split(':')?.pop();
                if (parkingSiteNameA < parkingSiteNameB) {
                  return -1;
                }
                if (parkingSiteNameA > parkingSiteNameB) {
                  return 1;
                }
                return 0;
              });
            });
            this.parkingSite = result;
            this.checkScrollToSpot();
          } else {
            this.parkingSite = null;
            if (this.targetScrollSpot) {
              this.targetScrollSpot = null;
              this.decrementRequests();
            }
          }
          this.decrementRequests();
        },
        error: error => {
          console.error('Error fetching parking sites from-to:', error);
          this.parkingSite = null;
          if (this.targetScrollSpot) {
            this.targetScrollSpot = null;
            this.decrementRequests();
          }
          this.decrementRequests();
        }
      });
  }

  dateRangeChange(event: any) {
    if (event.value !== null) {
      this.dateFrom = moment(this.dateSelectorGroup.controls["dateFrom"].value).format('YYYY-MM-DD');
      this.dateTo = moment(this.dateSelectorGroup.controls["dateTo"].value).format('YYYY-MM-DD');
      this.getParkingSite();
    }
  }

  parkingChange(event: any) {
    if (event.value !== null) {
      this.parkingId = event.value;
      this.getParkingSite();
    }
  }

  // Selecting a Card
  openOrder(booking: ParkingSiteSpotBooking, parkingSiteName: string) {
    // alert(parkingSiteName);
    this.booking = booking;
    this.checkoutService
      .getOrder(booking.orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(order => {
        const dialogRef = this.dialog.open(ParkingEditComponent, {
          // height: '850px',
          width: '720px',
          hasBackdrop: true,
          data: <OrderBooking>{ order, booking, parkingId: this.parkingId, parkingSiteName, email: order.customer.email }
        });

        dialogRef.componentInstance.saveChangesEvent.subscribe((updatedBooking: any) => {
          this.getParkingSite();
        });
      });
  }

  setParkingBookingMode(isChecked: boolean) {
    if (isChecked) {
      this.isParkingBookingMode = true;
      this.isParkingCheckinMode = false;
    } else {
      this.isParkingBookingMode = false;
      this.isParkingCheckinMode = true;
    }

    this.getParkingSite();
  }

  setParkingCheckinMode(isChecked: boolean) {
    if (isChecked) {
      this.isParkingBookingMode = false;
      this.isParkingCheckinMode = true;
    } else {
      this.isParkingBookingMode = true;
      this.isParkingCheckinMode = false;
    }

    this.getParkingSite();
  }

  onRowStatusClick(row: any, event: MouseEvent) {
    event.stopPropagation();
    this.statusContext = { type: 'row', data: row };
  }

  onSpotStatusClick(spot: any, row: any, event: MouseEvent) {
    event.stopPropagation();
    this.statusContext = { type: 'spot', data: spot, rowData: row };
  }

  async onStatusSelected(newStatus: string, context: any) {
    if (context.type === 'row') {
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        width: '400px',
        data: {
          title: 'PARKING_EDIT.STATUS_CHILDREN_TITLE',
          message: 'PARKING_EDIT.STATUS_CHILDREN_MESSAGE',
          confirmText: 'PARKING_EDIT.YES',
          cancelText: 'PARKING_EDIT.NO'
        }
      });

      dialogRef.afterClosed().pipe(takeUntil(this.componentDestroyed$)).subscribe(withChildren => {
        // undefined = dialog dismissed (backdrop/Esc) -> cancel the status change
        if (withChildren === undefined) {
          return;
        }
        this.updateParkingSiteStatus(this.organization.organizationId, context.data, newStatus === "Active", withChildren === true);
      });
    } else if (context.type === 'spot') {
      this.updateParkingSiteStatus(this.organization.organizationId, context.data, newStatus === "Active");
    }
  }

  updateParkingSiteStatus(orgId: string, parkingSite: any, newStatus: boolean, withChildren: boolean = false) {
    this.backOfficeService.updateParkingSite(orgId, parkingSite.parkingSiteId, newStatus, withChildren)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (updatedParkingSite: any) => {
          // if(updatedParkingSite){
          this.getParkingSite();
          this.snackBar.open('Status updated successfully', 'Close', { duration: 3000 });
          // }
        },
        error: (error: any) => {
          console.error('Error updating status:', error);
          this.snackBar.open('Failed to update status', 'Close', { duration: 3000 });
        }
      });
  }

  loadOverlappingParkings() {
    if (!this.organization || !this.organization.organizationId) {
      return;
    }
    this.incrementRequests();
    this.backOfficeService.getOverlappingParkings(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          this.rawOverlappingParkings = data || [];
          this.applyOverlappingFilters();
          this.decrementRequests();
        },
        error: (err) => {
          console.error('Error fetching overlapping parkings:', err);
          this.decrementRequests();
        }
      });
  }

  applyOverlappingFilters() {
    let filtered = [...this.rawOverlappingParkings];

    const search = (this.overlappingSearchText || '').trim().toLowerCase();
    if (search && this.overlappingSearchField) {
      filtered = filtered.filter(item => {
        const val = item[this.overlappingSearchField];
        return val && val.toLowerCase().includes(search);
      });
    }

    const fromStart = this.overlappingBookingFromRange.value.start;
    const fromEnd = this.overlappingBookingFromRange.value.end;
    if (fromStart) {
      const startMs = moment(fromStart).startOf('day').valueOf();
      filtered = filtered.filter(item => {
        if (!item.bookedFrom) return false;
        return moment(item.bookedFrom).valueOf() >= startMs;
      });
    }
    if (fromEnd) {
      const endMs = moment(fromEnd).endOf('day').valueOf();
      filtered = filtered.filter(item => {
        if (!item.bookedFrom) return false;
        return moment(item.bookedFrom).valueOf() <= endMs;
      });
    }

    const toStart = this.overlappingBookingToRange.value.start;
    const toEnd = this.overlappingBookingToRange.value.end;
    if (toStart) {
      const startMs = moment(toStart).startOf('day').valueOf();
      filtered = filtered.filter(item => {
        if (!item.bookedTo) return false;
        return moment(item.bookedTo).valueOf() >= startMs;
      });
    }
    if (toEnd) {
      const endMs = moment(toEnd).endOf('day').valueOf();
      filtered = filtered.filter(item => {
        if (!item.bookedTo) return false;
        return moment(item.bookedTo).valueOf() <= endMs;
      });
    }

    this.overlappingParkings = filtered;
  }

  clearOverlappingFilters() {
    this.overlappingSearchText = '';
    this.overlappingSearchField = 'parkingName';
    this.overlappingBookingFromRange.reset({ start: null, end: null }, { emitEvent: false });
    this.overlappingBookingToRange.reset({ start: null, end: null }, { emitEvent: false });
    this.applyOverlappingFilters();
  }

  goToCurrentParking(parkingId: string, spotName: string) {
    if (!parkingId) return;
    this.parkingId = parkingId;
    this.targetScrollSpot = spotName;
    this.incrementRequests();
    this.getParkingSite();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'current' },
      queryParamsHandling: 'merge'
    });
  }

  checkScrollToSpot() {
    if (!this.targetScrollSpot) {
      return;
    }
    const spotToScroll = this.targetScrollSpot;
    setTimeout(() => {
      const element = document.getElementById('spot-' + spotToScroll);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('highlight-flash');
        setTimeout(() => {
          element.classList.remove('highlight-flash');
        }, 3000);
      }
      this.decrementRequests();
    }, 250);
    this.targetScrollSpot = null;
  }

  goToOrder(orderId: string): void {
    if (!orderId) return;
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
